import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { useClients } from '@/hooks/useReference';
import { useInvoiceSettings } from '@/hooks/useSettings';
import type { Client } from '@/types';

interface UnbilledEntry {
  clientName: string | null;
  time: number;
}

/**
 * Unbilled hours and value per client. `/api/reports/uninvoiced` lists every
 * uninvoiced entry with its client's NAME (no id, no rate), so the rows are
 * grouped by name and priced at the client's rate, else the team default.
 */
export function useUnbilledByClient() {
  const clients = useClients().data ?? [];
  const defaultRate = useInvoiceSettings().data?.defaultHourlyRate ?? 0;
  const report = useQuery({
    queryKey: ['mobile', 'unbilled-by-client'],
    queryFn: ({ signal }) => get<{ entries?: UnbilledEntry[] }>('/api/reports/uninvoiced', undefined, signal),
    staleTime: 60_000
  });

  const byId = useMemo(() => {
    const hoursByName = new Map<string, number>();
    for (const e of report.data?.entries ?? []) {
      const k = e.clientName ?? '';
      hoursByName.set(k, (hoursByName.get(k) ?? 0) + (Number(e.time) || 0));
    }
    const m = new Map<string, { hours: number; amount: number }>();
    for (const c of clients) {
      const hours = hoursByName.get(c.name) ?? 0;
      m.set(c.id, { hours, amount: hours * rateOf(c, defaultRate) });
    }
    return m;
  }, [report.data, clients, defaultRate]);

  return { byId, isPending: report.isPending, refetch: report.refetch, defaultRate };
}

export function rateOf(c: Pick<Client, 'hourlyRate'> | undefined, fallback: number) {
  return c?.hourlyRate ?? fallback;
}

export const rateLabel = (rate: number) => `$${Number.isInteger(rate) ? rate : rate.toFixed(2)} / h`;

export const wholeMoney = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
